import React, { useState, useEffect } from 'react';
import { Cog6ToothIcon, LinkIcon, QrCodeIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';

const ItemReservations = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [preFillUrl, setPreFillUrl] = useState('');
  const [organizationSlug, setOrganizationSlug] = useState(null);

  // Start Time Buffer
  const [startTimeBuffer, setStartTimeBuffer] = useState(0);
  const [bufferSaving, setBufferSaving] = useState(false);
  const [isEditingBuffer, setIsEditingBuffer] = useState(false);
  const [bufferEditValue, setBufferEditValue] = useState(0);

  // Max Rental Duration
  const [maxRentalDuration, setMaxRentalDuration] = useState(0);
  const [durationSaving, setDurationSaving] = useState(false);
  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const [durationEditValue, setDurationEditValue] = useState(0);

  // Notification Flag
  const [notificationFlag, setNotificationFlag] = useState(0);
  const [flagSaving, setFlagSaving] = useState(false);
  const [isEditingFlag, setIsEditingFlag] = useState(false);
  const [flagEditValue, setFlagEditValue] = useState(0);

  // Fetch current settings and organization data
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const generalResponse = await axios.get('/api/settings/organization/general', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (generalResponse.data) {
          setStartTimeBuffer(generalResponse.data.startTimeBufferMinutes || 0);
          setMaxRentalDuration(generalResponse.data.maxRentalDurationHours || 0);
          setNotificationFlag(generalResponse.data.notificationFlagMinutes || 0);
        }

        // Fetch organization data to get slug
        const orgResponse = await axios.get('/api/settings/organization/contact', {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log('Organization response:', orgResponse.data);

        if (orgResponse.data && orgResponse.data.data && orgResponse.data.data.slug) {
          console.log('Setting organization slug:', orgResponse.data.data.slug);
          setOrganizationSlug(orgResponse.data.data.slug);
        } else {
          console.log('No slug found in response');
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [token]);

  // Build pre-fill URL based on organization slug or current domain
  useEffect(() => {
    if (organizationSlug) {
      // Use custom slug-based URL on main domain
      const url = `https://condrs.com/${organizationSlug}/register`;
      setPreFillUrl(url);
    } else {
      // Fallback to subdomain-based URL
      const protocol = window.location.protocol;
      const host = window.location.host;
      const url = `${protocol}//${host}/register`;
      setPreFillUrl(url);
    }
  }, [organizationSlug]);

  // Copy URL to clipboard
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(preFillUrl);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  // Download QR code as high-quality PNG
  const handleDownloadQR = () => {
    try {
      const svg = document.getElementById('customer-prefill-qr');
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      // Set canvas size for high quality (4x the display size)
      canvas.width = 800;
      canvas.height = 800;

      img.onload = () => {
        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the QR code
        ctx.drawImage(img, 0, 0, 800, 800);

        // Download the image
        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.download = 'customer-prefill-qr-code.png';
        downloadLink.href = pngFile;
        downloadLink.click();

        toast.success('QR code downloaded!');
      };

      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download QR code');
    }
  };

  // Handle start buffer edit button
  const handleEditBuffer = () => {
    setBufferEditValue(startTimeBuffer);
    setIsEditingBuffer(true);
  };

  // Handle cancel buffer edit
  const handleCancelBufferEdit = () => {
    setIsEditingBuffer(false);
    setBufferEditValue(startTimeBuffer);
  };

  // Handle save buffer
  const handleSaveBuffer = async () => {
    const value = parseInt(bufferEditValue, 10);
    if (isNaN(value) || value < 0) {
      toast.error('Buffer must be a positive number');
      return;
    }

    setBufferSaving(true);
    try {
      await axios.put(
        '/api/settings/organization/general',
        { startTimeBufferMinutes: value },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStartTimeBuffer(value);
      setIsEditingBuffer(false);
      toast.success('Start time buffer saved successfully');
    } catch (error) {
      console.error('Error saving start time buffer:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save start time buffer';
      toast.error(errorMessage);
    } finally {
      setBufferSaving(false);
    }
  };

  // Handle max rental duration edit button
  const handleEditDuration = () => {
    setDurationEditValue(maxRentalDuration);
    setIsEditingDuration(true);
  };

  // Handle cancel duration edit
  const handleCancelDurationEdit = () => {
    setIsEditingDuration(false);
    setDurationEditValue(maxRentalDuration);
  };

  // Handle save duration
  const handleSaveDuration = async () => {
    const value = parseInt(durationEditValue, 10);
    if (isNaN(value) || value < 0) {
      toast.error('Duration must be a positive number');
      return;
    }

    setDurationSaving(true);
    try {
      await axios.put(
        '/api/settings/organization/general',
        { maxRentalDurationHours: value },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMaxRentalDuration(value);
      setIsEditingDuration(false);
      toast.success('Max rental duration saved successfully');
    } catch (error) {
      console.error('Error saving max rental duration:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save max rental duration';
      toast.error(errorMessage);
    } finally {
      setDurationSaving(false);
    }
  };

  // Handle notification flag edit button
  const handleEditFlag = () => {
    setFlagEditValue(notificationFlag);
    setIsEditingFlag(true);
  };

  // Handle cancel flag edit
  const handleCancelFlagEdit = () => {
    setIsEditingFlag(false);
    setFlagEditValue(notificationFlag);
  };

  // Handle save flag
  const handleSaveFlag = async () => {
    const value = parseInt(flagEditValue, 10);
    if (isNaN(value) || value < 0) {
      toast.error('Notification flag must be a positive number');
      return;
    }

    setFlagSaving(true);
    try {
      await axios.put(
        '/api/settings/organization/general',
        { notificationFlagMinutes: value },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNotificationFlag(value);
      setIsEditingFlag(false);
      toast.success('Notification flag saved successfully');
    } catch (error) {
      console.error('Error saving notification flag:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save notification flag';
      toast.error(errorMessage);
    } finally {
      setFlagSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Item Reservations
          </h2>
          <p className="mt-1 text-gray-600">
            Configure settings specific to item reservations
          </p>
        </div>
      </div>

      {/* Settings Sections */}
      <div className="mt-8 space-y-6">
        {/* Reservation Settings */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Cog6ToothIcon className="h-6 w-6 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Reservation Settings</h3>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Configure default behaviors for reservations
          </p>

          <div className="space-y-4">
            {/* Start Time Buffer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time Buffer (minutes)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  max="1440"
                  value={isEditingBuffer ? bufferEditValue : startTimeBuffer}
                  onChange={(e) => setBufferEditValue(e.target.value)}
                  disabled={!isEditingBuffer || bufferSaving}
                  className={`w-32 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    !isEditingBuffer || bufferSaving ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''
                  }`}
                />
                <span className="text-sm text-gray-500">minutes</span>

                {!isEditingBuffer ? (
                  <button
                    onClick={handleEditBuffer}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveBuffer}
                      disabled={bufferSaving}
                      className={`inline-flex items-center px-3 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        bufferSaving ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {bufferSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelBufferEdit}
                      disabled={bufferSaving}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                When creating a new reservation, the start time will default to {startTimeBuffer} {startTimeBuffer === 1 ? 'minute' : 'minutes'} from the current time
              </p>
            </div>

            {/* Max Rental Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Rental Duration (hours)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  value={isEditingDuration ? durationEditValue : maxRentalDuration}
                  onChange={(e) => setDurationEditValue(e.target.value)}
                  disabled={!isEditingDuration || durationSaving}
                  className={`w-32 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    !isEditingDuration || durationSaving ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''
                  }`}
                />
                <span className="text-sm text-gray-500">hours</span>

                {!isEditingDuration ? (
                  <button
                    onClick={handleEditDuration}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveDuration}
                      disabled={durationSaving}
                      className={`inline-flex items-center px-3 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        durationSaving ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {durationSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelDurationEdit}
                      disabled={durationSaving}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Maximum rental duration in hours (0 = unlimited)
              </p>
            </div>

            {/* Notification Flag */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notification Flag (minutes)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  value={isEditingFlag ? flagEditValue : notificationFlag}
                  onChange={(e) => setFlagEditValue(e.target.value)}
                  disabled={!isEditingFlag || flagSaving}
                  className={`w-32 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    !isEditingFlag || flagSaving ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''
                  }`}
                />
                <span className="text-sm text-gray-500">minutes</span>

                {!isEditingFlag ? (
                  <button
                    onClick={handleEditFlag}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveFlag}
                      disabled={flagSaving}
                      className={`inline-flex items-center px-3 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        flagSaving ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {flagSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelFlagEdit}
                      disabled={flagSaving}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Flag reservations that are approaching max rental duration by this many minutes
              </p>
            </div>
          </div>
        </div>

        {/* Public Customer Information Form */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <LinkIcon className="h-6 w-6 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Public Customer Information Form</h3>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Share this link with customers so they can pre-fill their rental information before arriving
          </p>

          <div className="space-y-6">
            {/* URL Display and Copy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pre-Fill Form Link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={preFillUrl}
                  readOnly
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleCopyUrl}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ClipboardDocumentIcon className="h-5 w-5 mr-2" />
                  Copy
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Customers will use this form to enter their information. You can then look them up by their assigned ID when creating reservations.
              </p>
            </div>

            {/* QR Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                QR Code
              </label>
              <div className="space-y-4">
                <div className="inline-block p-4 bg-white border-2 border-gray-200 rounded-lg">
                  <QRCodeSVG
                    id="customer-prefill-qr"
                    value={preFillUrl}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <div>
                  <button
                    onClick={handleDownloadQR}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <QrCodeIcon className="h-5 w-5 mr-2" />
                    Download QR Code
                  </button>
                  <p className="mt-2 text-xs text-gray-500">
                    Download a high-quality QR code image (800x800px) that you can print or display
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemReservations;
